import React, { useCallback, useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import Container from "@material-ui/core/Container";
import { Typography, Link, Checkbox, Grid, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import publicMainStyle from "assets/jss/components/publicMainStyles";
import FooterMain from "./FooterMain";
import { PrivacyStatement } from "components/login/PrivacyStatement";
import toast from "react-hot-toast";
import { getTenantId } from "providers/TenantProvider";
import { addToast } from "components/notifications";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { TENANT_QUERY } from "features/common/queries";

const useStyles = makeStyles(publicMainStyle);

const PublicMain = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const { logo } = theme;
  const [openStatement, setOpenStatement] = useState(false);
  const [statementAgreed, setStatementAgreed] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const { data, loading } = useQueryWithErrorHandling(TENANT_QUERY, { variables: { tenantId: getTenantId() } });
  const tenantIsValid = !loading && Boolean(data?.tenant?.isValid);
  const userPrivacyAgreementNeeded = !loading && data?.tenant?.userPrivacyAgreementNeeded && tenantIsValid;

  const handleClick = useCallback(() => {
    if (userPrivacyAgreementNeeded)
      if (statementAgreed) history.push(`/login`);
      else setShowDisclaimer(true);
    else history.push(`/login`);
  }, [history, statementAgreed, userPrivacyAgreementNeeded]);

  const handleBackClick = useCallback(() => {
    setShowDisclaimer(false);
  }, []);

  const handleOpenStatement = useCallback(() => {
    setOpenStatement((prev) => !prev);
  }, []);

  const handleChangeValue = useCallback(() => {
    setStatementAgreed((prev) => !prev);
  }, []);

  useEffect(() => {
    toast.remove();
    if (!loading && !tenantIsValid) {
      addToast(t("General.TenantNotFound"), "error");
    }
  }, [tenantIsValid, t, loading]);

  useEffect(() => {
    if (loading) return;
    const autoLogout = localStorage.getItem("serverTriggeredLogout");
    if (autoLogout !== "true") return;

    addToast(t("Login.LoginToAnotherClient"), "error");
    localStorage.removeItem("serverTriggeredLogout");
  }, [loading, t]);
  return (
    <Container className={classes.root}>
      {openStatement ? (
        <PrivacyStatement onOpenStatement={handleOpenStatement} />
      ) : (
        <Container className={classes.loginForm} maxWidth="sm">
          <div className={classes.paper}>
            <img src={logo} alt="logo" className={classes.logo} />
            {userPrivacyAgreementNeeded ? (
              <Grid container direction="column" justifyContent="center" alignItems="center">
                {!showDisclaimer ? (
                  <>
                    <Typography component="h1">{t("Login.LoginMessage")}</Typography>
                    <Grid container direction="row" justifyContent="center" alignItems="center">
                      <Checkbox color="primary" checked={statementAgreed} onChange={handleChangeValue} />
                      <Typography variant="h5" className={classes.fontWeightBold}>
                        {`${t("Login.PrivacyStatementChecked")} `}
                        <Link onClick={handleOpenStatement} underline="always">
                          {`<${t("General.PrivacyStatement").toUpperCase()}>`}
                        </Link>
                      </Typography>
                    </Grid>
                    <Button
                      className={classes.login}
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleClick}
                      disabled={!tenantIsValid}
                    >
                      {t("Login.LoginButton")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography className={classes.fontWeightBold}>
                      {t("Login.ReadAndAgree") + t("General.PrivacyStatement").toUpperCase()}
                    </Typography>
                    <Button
                      className={classes.login}
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleBackClick}
                      disabled={!tenantIsValid}
                    >
                      {t("Login.Back")}
                    </Button>
                  </>
                )}
              </Grid>
            ) : (
              <>
                <Typography component="h1">{t("Login.LoginMessage")}</Typography>
                <Button
                  className={classes.login}
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleClick}
                  disabled={!tenantIsValid}
                >
                  {t("Login.LoginButton")}
                </Button>
              </>
            )}
          </div>
        </Container>
      )}
      {tenantIsValid && (
        <FooterMain
          openStatement={openStatement}
          onOpenStatement={handleOpenStatement}
          userPrivacyAgreementNeeded={userPrivacyAgreementNeeded}
        />
      )}
    </Container>
  );
};

export default PublicMain;
